/**
 * Fire-and-forget frontend error beacon. Reports critical client-side
 * failures (e.g. a gate that won't open) to the backend so admins can
 * see and triage them. Never throws, never blocks the UI.
 */
const API = process.env.REACT_APP_BACKEND_URL;

// Errors whose stack/source matches these markers come from browser
// extensions or injected third-party scripts (Safari masks them as
// `webkit-masked-url`), NOT from iEvntür code. We classify by origin and
// skip them so the admin feed reflects only genuine app errors.
const _THIRD_PARTY_MARKERS = [
  'webkit-masked-url',
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'safari-extension://',
  'anonymous>',
];

export function isThirdPartyErrorSource(text) {
  if (!text) return false;
  const t = String(text).toLowerCase();
  return _THIRD_PARTY_MARKERS.some((m) => t.includes(m));
}

// In-memory guards so a render loop or noisy console can't flood the beacon.
const _recent = new Map(); // signature -> lastSentMs
let _sessionCount = 0;
const _SESSION_CAP = 30;
const _DEDUPE_MS = 60000;

export function reportFrontendError({ context, message = '', detail = null } = {}) {
  try {
    if (!API || !context) return;
    if (_sessionCount >= _SESSION_CAP) return;
    const sig = `${context}|${String(message).slice(0, 120)}`;
    const now = Date.now();
    const last = _recent.get(sig);
    if (last && now - last < _DEDUPE_MS) return;
    _recent.set(sig, now);
    _sessionCount += 1;

    const token = localStorage.getItem('token');
    let app_build = null;
    try { app_build = localStorage.getItem('iEvntur.persistedBuildId') || null; } catch { /* noop */ }
    const payload = JSON.stringify({
      context,
      message: String(message || '').slice(0, 2000),
      detail: detail ? String(detail).slice(0, 8000) : null,
      path: (typeof window !== 'undefined' && window.location)
        ? window.location.pathname + window.location.search
        : null,
      app_build,
    });
    fetch(`${API}/api/frontend-errors`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
    }).catch(() => { /* beacon must never surface an error */ });
  } catch {
    /* swallow — telemetry is best-effort */
  }
}
